/**
 * 射場マスタ（Phase 1 確定版） — 日本のロケット射場 4件
 * =====================================================================
 * 本ファイルは Phase 1 の成果物。座標・空港・新幹線・公式URLは
 *   1) The Space Devs LL2 API の実レスポンス（docs/ll2-japan-locations.json）
 *   2) 出典付きの独立検証（docs/site-research-2026-07-25.json）＋ 相互クロスチェック
 * に基づき、推測を含まない。直線距離は下の haversineKm() で座標から算出した実測値。
 *
 * ★重要（Phase 2 のフィルタ根拠）:
 *   LL2 上に日本の射場は「ちょうど4件」しか存在せず、location id で確実に絞れる。
 *   country 系クエリ（例 pad__location__country__alpha_2_code=JP）は実測で不発だった。
 *   → 打ち上げ取得は LL2 `?location__ids=26,24,166,32&mode=normal` を使うこと。
 *     `mode=list` は pad.location が null になるので不可。
 *   詳細は docs/PHASE1-LL2-FINDINGS.md。
 */

/** 2点間の大圏（直線）距離 km。Phase 3 のホテル20km絞り込みでも同じ関数を使う。 */
export function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371.0088;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export interface Airport {
  iata: string;
  nameJa: string;
  lat: number;
  lon: number;
  /** 射場からの直線距離(km)。haversineKm(site, airport) で算出。表示用に0.1km丸め。 */
  straightLineKm: number;
  /** 航空便パネルの既定・到着空港にすべきか（=最寄りの現実的な到着地。乗継便は各比較サイトが補完する） */
  flightPanelDefault: boolean;
  /**
   * 表示上の役割。地図・アクセス欄の見せ方を分ける。
   *  - 'primary' … 現実的な最寄り（島内空港や射場に最も近い実用空港）＝主役表示
   *  - 'hub'     … 本土側の乗継ハブ（直行便はここまで／その先は乗継。例: 種子島に対する鹿児島KOJ）
   *  - 'alt'     … 遠いが便数の多い代替（例: 紀伊に対する関空KIX、北海道に対する新千歳CTS）
   * 「最寄り＝島内空港」なのに遠いハブを"推奨到着"と見せて誤誘導しないための区分。
   */
  role: 'primary' | 'hub' | 'alt';
  /** 空港→射場のアクセス（時間・手段・フェリー/連絡便の有無） */
  access: string;
  /**
   * 楽天レンタカーの「空港から検索」に渡す空港コード（`airport=` パラメータ）。
   * ★基本はIATAと同じだが**新千歳だけ SPK**（`CTS` を渡すとエラーページになるのを実測 2026-08-01）。
   *   楽天のトップ検索フォーム `select#dr-airport` の option 85件を全ダンプして採取した実値。
   */
  rakutenCarCode: string;
  /**
   * じゃらんレンタカーの「空港受取」に渡すコード（`/rentacar/search/AIR_<これ>/plan/`）。
   * じゃらんのトップ検索フォーム「空港」タブ `select[name=ra]` の option 76件を全ダンプして採取した実値。
   * ★存在しないコードは 404 になる（静かに全国結果へ化けることはない・実測 2026-08-01）。
   */
  jalanCarCode: string;
  /**
   * たびらいレンタカー（比較サイト）の検索地点。
   * `https://www.tabirai.net/car/<region>/search/rentacar.aspx?SAFG=<group>&SAF=<code>&SATG=<group>&SAT=<code>&STP=6&SYF=…&SYT=…`
   *
   * ★値は**サイト自身が出しているリンク／自サイトのフォーム送信結果**から採取した実値（2026-08-01）。
   *   `SAFG`(地点区分)は空港=1 だが**新千歳だけ 3**（ページが `newchitoseairport_area` ＝空港"周辺"エリア扱い）。
   * ★2026-07-27に「たびらいは条件を渡せない」と記録したが**誤り**だった。当時は推測したパラメータ名
   *   （`rentalDate` 等）で試して効かず「不可」と断じていた。実物は `SYF`/`SYT`。
   *   予約可能期間の上限も無い（+8か月の 2027-04-01 でも日付が反映されるのを実測）。
   */
  tabirai: { region: string; code: string; group: string };
  /**
   * ANAトラベラーズ ダイナミックパッケージ（航空券＋宿）の対象空港か。
   * ★ANAが就航していない空港を渡すと「行きの到着地を入力してください。(DWRN8031)」で
   *   条件が全部落ちた空フォームになる（＝HTTP 200 のまま静かに失敗する）。実測で
   *   KOJ/KIX/OBO/CTS=可、**TNE(種子島)・SHM(南紀白浜)=不可**（両空港ともJAL系のみ就航）。
   */
  anaDomtour: boolean;
  /**
   * ヤフーパック（宿＋航空券）のURLに入る航空会社。`/dp/<これ>/pref/…`。
   * ANA未就航の空港は 'jal' のみ。両社就航なら 'jal' を既定にする（JAL便の方が便数が多い区間のため）。
   */
  yahooAirline: 'jal' | 'ana';
}

/**
 * じゃらん（宿泊）のエリアコード3階層。`https://www.jalan.net/{kenCd}/LRG_{lrgCd}/[SML_{smlCd}/]`。
 * ★じゃらんのフォーム（kenCd選択でlrgCdが再構築される）と検索結果ページ内のエリア絞り込みリンクから
 *   実採取した値のみ（2026-08-01）。不整合なコードはHTTP200のまま「エラー画面」になるため、
 *   ステータスだけでは検知できない＝ここに推測値を入れないこと。
 */
export interface JalanArea {
  /** 都道府県コード 例 '460000'（鹿児島県） */
  kenCd: string;
  /** 大エリアコード 例 '461700'（離島） */
  lrgCd: string;
  /**
   * 小エリアコード。null = その大エリアに小区分が存在しない（LRGが最小粒度）。
   * ★種子島は必須。LRG_461700「離島」は奄美大島・屋久島・徳之島・与論島・甑島も含み、
   *   省くとエラーにならず**静かに別の島の宿が混ざる**（唯一の静かな失敗経路）。
   */
  smlCd: string | null;
}

export interface ShinkansenAccess {
  stationJa: string;
  lat: number;
  lon: number;
  /** 射場からの直線距離(km)。数百km/海隔てのケースを誤誘導しないため必ず併記する。 */
  straightLineKm: number;
  /**
   * 鉄道ルートとして現実的か。
   * false のとき UI は「新幹線は非現実的（海隔て/数百km）→空路推奨」を明示する。
   */
  isPractical: boolean;
  /** 駅→射場の現実的な所要時間・手段（新幹線がどこまでで、その先が何かを含む） */
  realisticTravel: string;
}

export interface OfficialSource {
  label: string;
  url: string;
}

export interface RakutenArea {
  /** largeClassCode は常に "japan" */
  largeClassCode: 'japan';
  /** 都道府県コード（Phase 3 で GetAreaClass を叩いて確定する。未確定は null） */
  middleClassCode: string | null;
  /** 地区コード（同上・複数可）。未確定は null */
  smallClassCodes: string[] | null;
}

export interface LaunchSite {
  /** アプリ内 slug */
  id: string;
  /** LL2 location id（打ち上げ突合・ホワイトリストのキー） */
  ll2LocationId: number;
  nameJa: string;
  nameEn: string;
  operator: string;
  vehicles: string[];
  /**
   * 所在の都道府県・市町村。schema.org/Place と Event の location.address に使う。
   * Google の Event 構造化データは location.address を必須としており、
   * geo だけでは全件が不適格になる（2026-07-31に判明）。
   * 値はいずれも当サイトが従来から本文で公開している表記そのままで、新規の事実ではない。
   */
  addressRegion: string;
  addressLocality: string;
  /** 検証済みの射点/複合施設セントロイド座標（表示・ホテル中心・地図に使う正） */
  lat: number;
  lon: number;
  /** 座標の出自・LL2/プロンプト概算との差の記録（監査用） */
  coordNote: string;
  /** 島＝到達にフェリー/連絡便が必須か（種子島のみ true） */
  isIsland: boolean;
  airports: Airport[];
  nearestShinkansen: ShinkansenAccess;
  /** 確定日の一次情報。LL2 が粗い/遅い日本便を補完するため UI に常時表示する。 */
  officialSources: OfficialSource[];
  /** 観覧場・要予約/抽選/チケット・立入規制などの実務メモ（アプリ表示用の要約） */
  viewingNotes: string;
  /** 延期が常態である旨の注意（予備日を勧める） */
  scrubCaveat: string;
  /**
   * 楽天トラベル区分コード検索用（Phase 3 で確定）。
   * 現時点は未確定＝ null。ここを推測で埋めない（GetAreaClass の実レスポンスで確定する）。
   */
  rakutenArea: RakutenArea;
  /**
   * るるぶトラベル（JTB系・国内宿専門）のエリアページ slug。`https://www.rurubu.travel/city/japan/<slug>`。
   * ★推測せず実描画で確定（2026-07-31）: 存在しない slug は 302→404ページになるため、
   *   200 かつ描画後タイトルと件数が出たものだけを採用した（種子島31件／肝付50件／串本157件／十勝361件）。
   *   大樹町は町単位のページが無く、所属する十勝エリアを採用。
   */
  rurubuCity: string;
  /**
   * るるぶトラベル（実体はAgodaホワイトラベル）の**検索用**エリアID。
   * `https://www.rurubu.travel/search?city=<これ>&checkIn=…&los=…&rooms=…&adults=…`
   * ★上の `rurubuCity`（slug）ページは**日付クエリを一切受け付けない**（HTTP200のまま
   *   「料金を確認するには、滞在日を指定してください」のまま＝静かな失敗）。日付を渡すならこちらのIDを使う。
   * 採取（2026-08-01）: slugページのHTMLに埋まっている `defaultSearchURL` と、
   *   実際に検索ボタンを押した遷移先URLの2系統が一致することを確認した実値。
   */
  rurubuCityId: number;
  /** じゃらん（宿泊）のエリアコード。レンタカーは空港コード（Airport.jalanCarCode）を使う。 */
  jalanArea: JalanArea;
  /**
   * 外部予約サイトに投げている**検索エリアの実名**（利用者向け表記）。
   * ★「射場周辺」と書くと半径で絞ったように読めるが、実際に問い合わせているのは
   *   宿・体験サイト側の市区町村〜観光エリア単位の区分1つ。見ていない範囲を「周辺」と言わないため、
   *   送客リンクの注記にはこの実名を出す（例: 北海道スペースポートの宿は実際には帯広・十勝の在庫）。
   */
  nearbyAreaJa: string;
  /**
   * アクティビティジャパンの県別ページのパス（`https://activityjapan.com/search/<これ>/`）。
   * ★実描画で確定（2026-07-31）。**存在しないパスでもHTTP 200が返り、静かに「日本全国」の結果へ
   *   フォールバックする**ため、ステータスでは判定できない。h1に県名が入るかで判別した
   *   （フォールバックの指紋＝h1に県名なし・19,665件）。実測: 鹿児島758件／和歌山246件／北海道1,060件。
   *   ⚠️地方名は「関西=kansai」（kinkiではない）。北海道は県ラベル無しの `hokkaido` 単体が正解。
   */
  activityJapanPath: string;
  /**
   * アソビュー！のエリアID（`/search/?destinationId=<これ>&destinationType=area`）。
   * ★2026-08-01の実測で、従来コメントの「県別URLの形式を特定できなかった」は**誤りだった**と判明。
   *   エリアページは `/area/` ではなく `/location/` 配下にあり、検索は `destinationId` で絞れる。
   * ⚠️ **destinationId が欠けると静かに「すべての行き先 4,013件」＝全国に化ける**（実測）。
   *   アクティビティジャパンと違いエリアがパスでなくクエリにあるため、URL組み立てで空になったら
   *   送客リンク自体を出さないこと。`destinationType` も実測した `area` 以外を使わない
   *   （推測で `city` にしたら全国化するのを実測）。
   */
  asoviewAreaId: string;
  /**
   * ヤフーパック（宿＋航空券）の検索エリア。
   * `https://travel.yahoo.co.jp/dp/<airline>/pref/<pref>/area/<wid>/?out_dep_date=…`
   *
   * ★2026-07-31に「条件プリセットは不可（POST＋_csrf必須）」と記録したが**誤り**だった。
   *   検索フォーム自体はPOSTだが、**送信後に着地する結果ページはGET**で、そのURLを直接組み立てられる。
   *   値は実際に検索を実行して遷移したURLと、目的地ピッカーの `data-wid` から採取した実値（2026-08-01）。
   * ★4射場すべてで、日付を2通り変えて描画に反映されることを確認済み（種子島もJAL便で通る）。
   */
  yahooPack: {
    /** 都道府県コード（JIS 2桁） 例 '46'=鹿児島 */
    pref: string;
    /** ヤフー独自のエリアコード 例 'EGAE'=種子島 */
    wid: string;
  };
}

/** 全射場に共通の注意（UIフッター等で使う） */
export const GLOBAL_CAVEAT =
  '日本の射場はいずれも天候・技術要因で延期が頻発します。予備日を2〜3日みて宿・レンタカー・航空便を確保してください。' +
  'フェリー・観覧抽選・観覧チケットの受付は打ち上げの約1か月前に始まることが多く、確定日は各射場の公式ページで確認してください。';

export const LAUNCH_SITES: LaunchSite[] = [
  {
    id: 'tanegashima',
    ll2LocationId: 26,
    nameJa: '種子島宇宙センター（吉信射点 LA-Y）',
    nameEn: 'Tanegashima Space Center (Yoshinobu LC)',
    operator: 'JAXA / 三菱重工（MHI）',
    addressRegion: '鹿児島県',
    addressLocality: '南種子町',
    vehicles: ['H3', 'H-IIA（退役）'],
    lat: 30.40222,
    lon: 130.975,
    coordNote:
      'LL2(30.4,130.97) は吉信射点(LA-Y)の丸め値。Wikipedia 30°24′08″N130°58′30″E=30.40222,130.97500 と約250m以内で一致。LA-Y1/Y2 のパッド別座標は非公開のため複合施設セントロイドを採用。プロンプト概算30.40,130.97とも一致。',
    isIsland: true,
    airports: [
      {
        iata: 'KOJ',
        nameJa: '鹿児島空港',
        lat: 31.80333,
        lon: 130.71694,
        straightLineKm: 157.7,
        flightPanelDefault: false,
        role: 'hub', // 本土側の乗継ハブ。最寄りではない（島内TNEが最寄り）。東京等からはまずKOJ→TNEへ乗継
        access:
          '本土（九州）側の乗継ハブ。ここから (a) 空路で種子島空港(TNE)へ約35分乗継＋レンタカー約45分、または (b) 鹿児島港からToppy&Rocket高速船で西之表港へ約1時間35分（要事前予約）＋レンタカー約1〜1.5時間。※種子島への直行便は無いため、東京等からはKOJ経由が一般的。',
        rakutenCarCode: 'KOJ',
        jalanCarCode: 'A022',
        tabirai: { region: 'kagoshima', code: '146201', group: '1' },
        anaDomtour: true,
        yahooAirline: 'jal',
      },
      {
        iata: 'TNE',
        nameJa: '種子島空港（コスモポート種子島）',
        lat: 30.605,
        lon: 130.99167,
        straightLineKm: 22.6,
        flightPanelDefault: true,
        role: 'primary', // 島内・射場に最も近い実用空港＝現実的な最寄り。乗継便は比較サイトが自動で補完
        access:
          '島内（中央東部）。射場（島南端・南種子）までレンタカーで国道58号を約40〜50分。レンタカーは実質必須だが空港には2社のみ（種子島交通・大和交通）＝打ち上げ前後は早期予約。定期便は鹿児島(KOJ)から約35分、季節限定で大阪伊丹(J-Air)。',
        rakutenCarCode: 'TNE',
        jalanCarCode: 'A051',
        tabirai: { region: 'kagoshima', code: '146204', group: '1' },
        anaDomtour: false, // ANA未就航（JAC/JAL系のみ）。渡すと DWRN8031 で条件が落ちる
        yahooAirline: 'jal',
      },
    ],
    nearestShinkansen: {
      stationJa: '鹿児島中央',
      lat: 31.5827,
      lon: 130.5416,
      straightLineKm: 137.6,
      isPractical: false,
      realisticTravel:
        '鉄道単独では不可（本土と種子島は外洋で隔たる）。鹿児島中央→鹿児島本港南埠頭(市電/タクシー約15分)→Toppy&Rocket高速船で西之表約1時間35分(要事前予約)→レンタカー約1〜1.5時間。合計3.5〜4.5時間＋便待ち。KOJ→TNEの空路乗継が速い＝空路推奨。',
    },
    officialSources: [
      { label: 'JAXA ファン!ファン!JAXA! カウントダウン（打ち上げ情報ハブ）', url: 'https://fanfun.jaxa.jp/countdown/' },
      { label: 'JAXA 種子島 打上げ見学場ガイド', url: 'https://fanfun.jaxa.jp/visit/tanegashima/launch.html' },
      /*
       * 南種子町のロケット情報ハブ。回ごとの子ページ（H3f9_2.html 等）を列挙している親ページ。
       * ★2026-08-02: 旧 `/sightseeing/H3rocket.html` が **404** になっていたので差し替えた（実ブラウザで実測）。
       *   子ページを直接指すと打ち上げのたびに死ぬので、**回に依存しないハブ**を指す。
       * ⚠️ `http://` なのは意図的。この自治体サイトは **https が証明書エラー**
       *   （ERR_SSL_UNRECOGNIZED_NAME_ALERT）で開けない。外部リンクなので混在コンテンツにはならない。
       */
      { label: '南種子町 ロケット打ち上げ関連情報（確定日＋観覧導線）', url: 'http://www.town.minamitane.kagoshima.jp/sightseeing/rocket.html' },
      { label: '種子島観光協会 ロケット打ち上げ', url: 'https://tanekan.jp/rocket/' },
      { label: '三菱重工 宇宙（H3商業打上げ）', url: 'https://www.mhi.com/products/space/' },
    ],
    viewingNotes:
      '観覧場は南種子町管理の4か所。打ち上げ時は宇宙センター＋射点半径3kmが立入禁止で、この4公園だけカウントダウン音声を中継。①恵美之江展望公園＝最も近く正面だが唯一の要予約地（南種子町への事前抽選申込が必要）。②長谷公園（斜面で見やすい・予約不要）③宇宙ヶ丘公園（町に最も近い・キャンプ場併設・予約不要）④前之峯グランド（予約不要）。ホテル・レンタカーは打ち上げ前に満杯＝数週間前に確保。鹿児島からの高速船Toppy&Rocketは事前予約制（約1か月前開始・出発20分前チェックイン・悪天欠航あり）。',
    scrubCaveat: 'H3は天候・技術要因で延期・スクラブが頻発（予備期間が数週間に及ぶことも）。予備日を2〜3日確保。',
    // GetAreaClass の実レスポンスで確定（2026-07-31）: [small] ritou = 「種子島」（島全体で1区分）
    rakutenArea: { largeClassCode: 'japan', middleClassCode: 'kagoshima', smallClassCodes: ['ritou'] },
    rurubuCity: 'tanegashima',
    rurubuCityId: 711974,
    nearbyAreaJa: '種子島',
    jalanArea: { kenCd: '460000', lrgCd: '461700', smlCd: '461705' },
    activityJapanPath: 'kyusyu/kagoshima/336',
    asoviewAreaId: 'are0462600',
    yahooPack: { pref: '46', wid: 'EGAE' }, // 種子島
  },
  {
    id: 'uchinoura',
    ll2LocationId: 24,
    nameJa: '内之浦宇宙空間観測所（USC・M射点）',
    nameEn: 'Uchinoura Space Observation Center (USC)',
    operator: 'JAXA（ISAS）',
    addressRegion: '鹿児島県',
    addressLocality: '肝付町',
    vehicles: ['イプシロン', 'イプシロンS'],
    lat: 31.251,
    lon: 131.0821,
    coordNote:
      'LL2(31.251944,131.081944)は施設セントロイド。実際のイプシロン射点(旧M-V/Mセンター)は約31.2510,131.0821（LL2点の約105m南）。施設は約0.7km²のコンパクトな段丘で差は観覧上無視できる。プロンプト概算31.25,131.08とも一致。',
    isIsland: false,
    airports: [
      {
        iata: 'KOJ',
        nameJa: '鹿児島空港',
        lat: 31.8034,
        lon: 130.7194,
        straightLineKm: 70.4,
        flightPanelDefault: true,
        role: 'primary', // 内之浦は本土・唯一の実用空港＝これが現実的な最寄り
        access:
          '唯一の実用空港（南東へ約100km道路）。レンタカー強く推奨で約2時間〜2時間10分（九州道→鹿屋/笠之原IC→国道68/448号、終盤約30kmは海沿いの狭路）。鉄道は射場に達しない。公共交通は空港連絡バス→鹿屋バスセンター→内之浦行き路線バス約80分→タクシー＝片道半日で本数僅少。',
        rakutenCarCode: 'KOJ',
        jalanCarCode: 'A022',
        tabirai: { region: 'kagoshima', code: '146201', group: '1' },
        anaDomtour: true,
        yahooAirline: 'jal',
      },
    ],
    nearestShinkansen: {
      stationJa: '鹿児島中央',
      lat: 31.5827,
      lon: 130.5416,
      straightLineKm: 63.2,
      isPractical: true,
      realisticTravel:
        '本土（フェリー不要）だが射場は鹿児島湾の対岸・大隅半島側で道路距離は遠い。レンタカーで約2.5〜3時間（桜島フェリー約15分＋大隅半島を南下、または湾を迂回して約110km）。大隅半島に鉄道は無く、実質「駅からレンタカー」。多くの人はKOJ空路＋レンタカーの方が同等以上に速く単純＝新幹線は代替扱い。',
    },
    officialSources: [
      { label: 'JAXA イプシロンロケット トピックス（確定打上げ告知）', url: 'https://www.jaxa.jp/projects/rockets/epsilon/topics_j.html' },
      { label: 'JAXA プレスリリース（公式の日程・結果）', url: 'https://www.jaxa.jp/press/index_j.html' },
      { label: 'ISAS/JAXA 内之浦宇宙空間観測所 施設ページ', url: 'https://www.isas.jaxa.jp/about/facilities/uchinoura.html' },
      { label: '肝付町観光（打上げごとの観覧場・道路規制告知）', url: 'https://kankou-kimotsuki.net/' },
    ],
    viewingNotes:
      'USC構内からは観覧不可（打ち上げ日は一般閉鎖）。肝付町がJAXAの日程確定後に公式観覧場を告知する。定番は宮原ロケット見学場（国道448号沿い・射点の約3km南の高台）。打ち上げ日は周辺で交通規制。本土のためフェリー予約不要が種子島に対する利点。ただし宿が非常に限られる＝肝付/鹿屋、または鹿児島市で早期確保。無料の宇宙科学資料館は非打上げ日向け。',
    scrubCaveat: 'イプシロンSも天候・技術で延期し得る。予備日を2〜3日確保。',
    // GetAreaClass の実レスポンスで確定（2026-07-31）: [small] kanoya =「鹿屋・垂水・志布志」。
    // 射場のある肝付町は大隅半島南部＝この区分。なお [small] oosumi は名前に反して
    // 「霧島・国分・鹿児島空港」＝射場から約70kmで20km圏外のため入れない。
    rakutenArea: { largeClassCode: 'japan', middleClassCode: 'kagoshima', smallClassCodes: ['kanoya'] },
    rurubuCity: 'kimotsuki',
    rurubuCityId: 720840,
    nearbyAreaJa: '鹿屋・垂水・大隅',
    jalanArea: { kenCd: '460000', lrgCd: '460800', smlCd: null },
    activityJapanPath: 'kyusyu/kagoshima/331',
    asoviewAreaId: 'are0460800',
    yahooPack: { pref: '46', wid: 'EGAC' }, // 霧島・大隅半島（内之浦のある肝付町を含む）
  },
  {
    id: 'spaceport_kii',
    ll2LocationId: 166,
    nameJa: 'スペースポート紀伊（串本）',
    nameEn: 'Spaceport Kii (Kushimoto)',
    operator: 'スペースワン',
    addressRegion: '和歌山県',
    addressLocality: '串本町',
    vehicles: ['カイロス（KAIROS）'],
    lat: 33.54422,
    lon: 135.8895,
    coordNote:
      'LL2(33.5443,135.8895)が正。英語版Wikipedia・spacelaunchschedule.com(33.544224,135.889502)と3系統一致し、浦神半島先端(串本町田原/那智勝浦町浦神)の実射点を指す。★プロンプト概算33.46,135.90は約9km南＝太平洋上で誤り。LL2値を採用。',
    isIsland: false,
    airports: [
      {
        iata: 'SHM',
        nameJa: '南紀白浜空港（熊野白浜リゾート空港）',
        lat: 33.66222,
        lon: 135.36444,
        straightLineKm: 50.4,
        flightPanelDefault: true,
        role: 'primary', // 射場に最も近い実用空港
        access:
          '最寄り。レンタカーで約1時間20分〜1時間40分（約65〜70km・国道42号/紀勢道を南下し串本・田原へ）。空港前にレンタカー各社。羽田等から定期便。鉄道派はJR白浜駅→特急くろしお→串本→普通で紀伊田原/紀伊浦神と乗継可（本数少）。',
        rakutenCarCode: 'SHM',
        jalanCarCode: 'A043',
        tabirai: { region: 'wakayama', code: '130201', group: '1' },
        anaDomtour: false, // ANA未就航（JAL/J-AIRのみ）
        yahooAirline: 'jal',
      },
      {
        iata: 'KIX',
        nameJa: '関西国際空港',
        lat: 34.43056,
        lon: 135.23028,
        straightLineKm: 115.8,
        flightPanelDefault: false,
        role: 'alt', // 遠いが便数が多い代替
        access:
          '便数は多いが遠い。レンタカーで約3〜3.5時間（約190km）。鉄道は関空快速→乗換→特急くろしお→串本→普通で約4〜4.5時間。距離重視ならSHMの方が近く速い。',
        rakutenCarCode: 'KIX',
        jalanCarCode: 'A003',
        tabirai: { region: 'osaka', code: '127201', group: '1' },
        anaDomtour: true,
        yahooAirline: 'jal',
      },
    ],
    nearestShinkansen: {
      stationJa: '新大阪',
      lat: 34.7333,
      lon: 135.5003,
      straightLineKm: 137.0,
      isPractical: true,
      realisticTravel:
        '大阪起点の標準ルート（公式・JTBもJR推奨）。ただし新幹線が行くのは新大阪までで、その先は特急くろしおで串本まで約3時間30分＋JR紀勢本線の普通に乗換約13〜15分で紀伊田原/紀伊浦神、徒歩で見学場。合計およそ4時間＝「新幹線で直行」ではない点をUIで明示。遠方・時間優先なら南紀白浜空港(SHM)へ空路＋レンタカー約1.5時間が同等以上に速い。',
    },
    officialSources: [
      { label: 'スペースワン公式 プレスリリース（『カイロス○号機の打上げ日の設定について』が一次情報）', url: 'https://www.space-one.co.jp/news/' },
      { label: 'スペースワン公式トップ', url: 'https://www.space-one.co.jp/' },
      { label: 'カイロスロケット打上げ応援サイト（JTB和歌山・公式見学場/チケット/日程 073-432-5860）', url: 'https://wakayama-rocket.com/' },
      { label: '串本町 スペースポート紀伊 案内', url: 'https://www.town.kushimoto.wakayama.jp/sangyo/rocket/2021-1108-1334-11.html' },
      { label: 'スペースワン公式X（当日速報）', url: 'https://x.com/Kairos_SPACEONE' },
    ],
    viewingNotes:
      '公式見学場は2か所（各定員2,000人・射点から約2km）。①田原海水浴場（串本町側・JR紀伊田原駅から徒歩約9分）②旧浦神小学校（那智勝浦町側・JR紀伊浦神駅から徒歩約2分）。入場は打ち上げごとに販売の有料チケット/ツアー（JTBの日帰りバスツアーor宿泊プラン）またはJR利用が前提。パーク&ライド無し・見学場周辺に駐車場無し・国道42号は一本道で大渋滞必至＝JR＋徒歩を強く推奨。紀伊田原/紀伊浦神は特急くろしおが停まらない普通のみの小駅（串本で乗換）。事前予約必須。',
    scrubCaveat: '天候スクラブが頻発（カイロス3号機も悪天で延期）。冬季（過去実績3月・12月）は海沿いで強風・冷え込み。予備日を2〜3日確保。',
    // GetAreaClass の実レスポンスで確定（2026-07-31）: [small] Katsuura =「勝浦・串本・すさみ」。
    // ⚠️ コードの先頭が大文字（Katsuura）。楽天の実レスポンスどおりで、小文字にすると一致しない。
    rakutenArea: { largeClassCode: 'japan', middleClassCode: 'wakayama', smallClassCodes: ['Katsuura'] },
    rurubuCity: 'kushimoto',
    rurubuCityId: 108696,
    nearbyAreaJa: '串本・勝浦',
    jalanArea: { kenCd: '300000', lrgCd: '301100', smlCd: '301102' },
    activityJapanPath: 'kansai/wakayama/233',
    asoviewAreaId: 'are0301100',
    yahooPack: { pref: '30', wid: 'DAAC' }, // 白浜・串本・那智勝浦・熊野
  },
  {
    id: 'hokkaido_spaceport',
    ll2LocationId: 32,
    nameJa: '北海道スペースポート（HOSPO・大樹町）',
    nameEn: 'Hokkaido Spaceport (HOSPO), Taiki',
    operator: 'SPACE COTAN / インターステラテクノロジズ（IST）',
    addressRegion: '北海道',
    addressLocality: '大樹町',
    vehicles: ['ZERO（IST）', 'MOMO（IST）', 'JAXA観測ロケット'],
    lat: 42.5,
    lon: 143.44139,
    coordNote:
      'LL2(42.5,143.441389)は大樹町多目的航空公園の登録施設セントロイド（緯度42°30′00″の丸め値）で、実際の射点(LC-0/LC-1)は海側へ約1〜2km。射点の4〜5桁座標は運営が未公開・第三者DBも不一致のため、計画用にはLL2セントロイドを採用。プロンプト概算42.51,143.44とも一致。',
    isIsland: false,
    airports: [
      {
        iata: 'OBO',
        nameJa: 'とかち帯広空港',
        lat: 42.7333,
        lon: 143.2172,
        straightLineKm: 31.8,
        flightPanelDefault: true,
        role: 'primary', // 射場に最も近い実用玄関口
        access:
          '最寄り＆実用玄関口。レンタカーで約40〜55分（約55〜60km・国道236号/道道238号を南下）。射点/公園への直通バスは無く、レンタカー強く推奨（空港にトヨタ/タイムズ/日産/ニッポン/オリックス）。国内線のみ（主に羽田約1時間35分）。',
        rakutenCarCode: 'OBO',
        jalanCarCode: 'A025',
        tabirai: { region: 'hokkaido', code: '101204', group: '1' },
        anaDomtour: true,
        yahooAirline: 'ana',
      },
      {
        iata: 'CTS',
        nameJa: '新千歳空港',
        lat: 42.7752,
        lon: 141.6923,
        straightLineKm: 146.3,
        flightPanelDefault: false,
        role: 'alt', // 遠いが便数の多い代替（OBOに適便が無い場合）
        access:
          '道内主要ハブ。OBOに適便が無い場合の代替。最寄りではなく約190〜210km・車で約2.5〜3時間（道東道経由）。帯広へ乗継後にレンタカーでも可。フォールバック用途。',
        rakutenCarCode: 'SPK',
        jalanCarCode: 'A005',
        tabirai: { region: 'hokkaido', code: '101101', group: '3' }, // 新千歳のみ SAFG=3（空港周辺エリア扱い）
        anaDomtour: true,
        yahooAirline: 'ana',
      },
    ],
    nearestShinkansen: {
      stationJa: '新函館北斗',
      lat: 41.904694,
      lon: 140.648361,
      straightLineKm: 239.4,
      isPractical: false,
      realisticTravel:
        '非現実的。北海道新幹線の現北端（北斗市・道南）で、大樹町から約240km直線・道路で約380〜420km/5.5〜6.5時間、以東に鉄道は続かない。東京から新幹線経由だと鉄道約4時間＋車約6時間≒10時間。空路(羽田→帯広約1時間35分＋車約40〜55分)の約2.5時間に比べ論外＝空路推奨。札幌延伸(2031頃)後も射場は新幹線から遠い東側のまま。',
    },
    officialSources: [
      { label: 'HOSPO 公式 Missions（今後/完了の打上げ）', url: 'https://hokkaidospaceport.com/en/missions/' },
      { label: 'HOSPO 公式 Launch 概要（LC-0/LC-1・運営SPACE COTAN）', url: 'https://hokkaidospaceport.com/en/launch/' },
      { label: 'インターステラテクノロジズ ZERO（LC-1優先運用）', url: 'https://www.istellartech.com/en/launch/zero' },
      // ★2026-08-02: 旧 `/en_news` が **404** になっていたので差し替えた（実ブラウザで実測）。
      //   後継は日本語の `/news`（当サイトの利用者は日本語話者なのでこちらが適切）。
      { label: 'インターステラテクノロジズ ニュース（打上げ告知）', url: 'https://www.istellartech.com/news' },
      { label: '大樹町 北海道スペースポート(HOSPO)ページ', url: 'https://www.town.taiki.hokkaido.jp/soshiki/kokuuchusuishinshitsu/1/850.html' },
    ],
    viewingNotes:
      '本土（フェリー不要）・帯広から車で入る。SPACE COTAN＋ISTが打ち上げごとに観覧場/チケットを設定＝各回で要確認。実績: ①IST公式「SKY-HILLS（スカイヒルズ）」射点から約4km・正面視界良・通常有料。②大樹町多目的航空公園での無料中継。③宇宙交流センターSORA＝無料・予約不要・概ね4〜10/11月開館（予約不要で入れる唯一のHOSPO施設）。打ち上げ日は射点周辺が危険区域＝私有地・閉鎖道路に入らない（過去に道が明示警告）。駐車場が限られ早着必須。',
    scrubCaveat: '冬季は積雪・厳寒で屋外観覧困難、初夏は太平洋岸の海霧でスクラブ多発。延期頻発＝予備日を2〜3日確保し、SORA等の開館期間(夏季中心)も確認。',
    // GetAreaClass の実レスポンスで確定（2026-07-31）: [small] obihiro =「帯広・十勝」。
    // 射場のある大樹町は十勝管内＝この区分（[small] hidaka「日高・えりも」は別管内なので入れない）。
    rakutenArea: { largeClassCode: 'japan', middleClassCode: 'hokkaido', smallClassCodes: ['obihiro'] },
    rurubuCity: 'tokachi',
    rurubuCityId: 718284,
    nearbyAreaJa: '帯広・十勝',
    jalanArea: { kenCd: '010000', lrgCd: '013200', smlCd: null },
    activityJapanPath: 'hokkaido/hokkaido_e/013',
    asoviewAreaId: 'are0013200',
    yahooPack: { pref: '01', wid: 'ABAH' }, // 釧路・帯広・網走・知床（大樹町の宿は帯広・十勝の在庫）
  },
];

/** LL2 打ち上げ取得の location__ids フィルタ用（= [26,24,166,32]）。 */
export const LL2_LOCATION_IDS: number[] = LAUNCH_SITES.map((s) => s.ll2LocationId);

/** LL2 の location id → 射場マスタ。取得した打ち上げを射場に突合するのに使う。 */
export const SITE_BY_LOCATION_ID: ReadonlyMap<number, LaunchSite> = new Map(
  LAUNCH_SITES.map((s) => [s.ll2LocationId, s]),
);

export function getSiteByLocationId(locationId: number): LaunchSite | undefined {
  return SITE_BY_LOCATION_ID.get(locationId);
}

export function getSiteById(id: string): LaunchSite | undefined {
  return LAUNCH_SITES.find((s) => s.id === id);
}

// =====================================================================
// 射場ガイド用データ（guideページ）
// ---------------------------------------------------------------------
// 各射場の観覧スポット・リード文。★上の viewingNotes（出典付き）の記述だけを
// 構造化したもので、距離/駐車台数など"数値の裏付けが無い値は入れない"。
// （ハンドオフのサンプルにあった「射場から3.0km・駐車60台」等は本物の検証値が
// 無いため採用せず、prose に書かれた性質だけを memo にしている＝誠実性ルール#5）。
// =====================================================================
export interface SiteGuideSpot {
  name: string;
  memo: string;
  /** 「定番」「公式」など */
  badge?: string;
}
export interface SiteGuide {
  /** ガイド冒頭のリード文（1〜2文） */
  lead: string;
  /** 宿の目安（地域・定性的。km は検証値が無いため出さない） */
  lodgingArea: string;
  /** 観覧場所サマリーの補足（"車必須" など） */
  viewingAccessHint: string;
  spots: SiteGuideSpot[];
}

export const SITE_GUIDES: Record<string, SiteGuide> = {
  tanegashima: {
    lead:
      '日本最大の打ち上げ場。海に面した吉信射点を、南種子町内の公園から観覧します。打ち上げ当日は宇宙センターと射点半径3kmが立入禁止で、指定の観覧公園だけがカウントダウン音声を中継します。',
    lodgingArea: '南種子・西之表',
    viewingAccessHint: '車必須',
    spots: [
      { name: '恵美之江展望公園', memo: '最も近く射点の正面。唯一の要予約地で、南種子町への事前抽選申込が必要。', badge: '定番' },
      { name: '長谷公園', memo: '斜面で視界が開け見やすい。予約不要。' },
      { name: '宇宙ヶ丘公園', memo: '町に最も近くキャンプ場併設。家族向けで予約不要。' },
      { name: '前之峯グランド', memo: '予約不要の観覧場所。混雑時の選択肢。' },
    ],
  },
  uchinoura: {
    lead:
      '大隅半島の段丘にあるイプシロン系の射場。USC構内は打ち上げ日は一般閉鎖のため、肝付町がJAXAの日程確定後に告知する公式観覧場から観覧します。本土のためフェリー予約は不要です。',
    lodgingArea: '肝付町・鹿屋／鹿児島市',
    viewingAccessHint: '車必須・宿は僅少',
    spots: [
      { name: '宮原ロケット見学場', memo: '定番。国道448号沿い、射点の約3km南の高台。打ち上げ日は周辺で交通規制。', badge: '定番' },
      { name: '肝付町の公式観覧場', memo: '打ち上げごとに肝付町が観覧場所を告知。各回で必ず公式を確認。' },
      { name: '宇宙科学資料館', memo: '無料の見学施設（非打ち上げ日向け）。打ち上げ観覧場そのものではない。' },
    ],
  },
  spaceport_kii: {
    lead:
      '串本町・浦神半島先端にあるスペースワン「カイロス」の射場。公式見学場は2か所（各定員2,000人・射点から約2km）。パーク&ライド無し・周辺に駐車場無しで、JR＋徒歩が前提です。',
    lodgingArea: '串本・那智勝浦',
    viewingAccessHint: 'JR＋徒歩・要チケット',
    spots: [
      { name: '田原海水浴場（串本町側）', memo: '公式見学場。JR紀伊田原駅から徒歩約9分。入場は打ち上げごとの有料チケット/ツアーが前提。', badge: '公式' },
      { name: '旧浦神小学校（那智勝浦町側）', memo: '公式見学場。JR紀伊浦神駅から徒歩約2分。同じく有料チケット/ツアーが前提。', badge: '公式' },
    ],
  },
  hokkaido_spaceport: {
    lead:
      '大樹町の北海道スペースポート（HOSPO）。本土でフェリー不要、帯広から車で入ります。SPACE COTANとISTが打ち上げごとに観覧場/チケットを設定するため、各回で必ず確認してください。',
    lodgingArea: '大樹町・帯広',
    viewingAccessHint: '車必須・要確認',
    spots: [
      { name: 'SKY-HILLS（スカイヒルズ）', memo: 'IST公式の観覧場。射点から約4kmで正面の視界が良い。通常は有料。', badge: '定番' },
      { name: '大樹町多目的航空公園', memo: '無料中継が行われる実績あり。射点そのものではない。' },
      { name: '宇宙交流センターSORA', memo: '無料・予約不要。概ね4〜10/11月の開館で、予約不要で入れる唯一のHOSPO施設。' },
    ],
  },
};

export function getGuideById(id: string): SiteGuide | undefined {
  return SITE_GUIDES[id];
}
